import React, { useCallback, useState, useMemo, useEffect } from "react";
import {
  IoPencil,
  IoChevronDownOutline,
  IoChevronUpOutline
} from "react-icons/io5";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

// MUI
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";

// MUI ICONS
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MenuIcon from '@mui/icons-material/Menu';

// Draggable
import { DndContext, useDroppable } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Context
import { useCoursesNewContext } from "../../../context";

// Components
import Spinner from "../../../../../../components/spinner";
import Typography from "../../../../../../components/Typography";
import SubRow from "./SubRow";

// Styles
import styles from "./style.module.scss";

// Utils
import { subUnitsUrl, subUnitUrl } from "../../../../../../utils/urls";

// Types
import { Unit, SubUnit } from "../../../../../../types";

interface UnitRowProps {
  index: number;
  unit: Unit;
  onEdit: (id: string | number) => void;
  onDelete: (id: string | number) => void;
}

const UnitRow: React.FC<UnitRowProps> = ({ index, unit, onEdit, onDelete }) => {
  // i18n
  const { t } = useTranslation();

  // Props
  const { course } = useCoursesNewContext();

  // Drag and Drop
  const {
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transition,
    transform
  } = useSortable({
    id: `unit_${unit.id.toString()}`,
    data: {
      type: 'unit',
      id: unit.id.toString()
    }
  });

  const { setNodeRef: setNodeRefDroppable } = useDroppable({ id: 'sub_units_of_' + unit.id.toString() });

  // States
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  const [subUnits, setSubUnits] = useState<SubUnit[]>([]);

  // Methods
  const getSubUnits = useCallback(async () => {
    setLoading(true);
    try {
      const options = {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      };

      const response = await fetch(subUnitsUrl(course?.id || '', unit.id), options);
      const data = await response.json();
      setSubUnits(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [course?.id, unit.id]);

  // useEffect
  useEffect(() => {
    getSubUnits();
  }, [getSubUnits]);

  useEffect(() => {
    // TODO: IMPLEMENT CALL TO THE BACKEND TO UPDATE THE ORDER OF THE units
  }, [unit.start_date]);

  // handlers
  const handleEdit = useCallback(() => {
    onEdit(unit.id);
  }, [unit.id, onEdit]);

  const handleExpand = useCallback(async () => {
    // load sub units if not expanded
    if (!isExpanded) await getSubUnits();
    // toggle expansion
    setIsExpanded(!isExpanded);
  }, [isExpanded, getSubUnits]);

  const handleDeleteSubUnit = useCallback(async (success: boolean) => {
    if (success) await getSubUnits();
  }, [getSubUnits]);

  const handleSaveSubUnit = useCallback(async (success: boolean) => {
    if (success) await getSubUnits();
  }, [getSubUnits]);

  const handleAddSubUnit = useCallback(() => {
    setSubUnits(prev => [...prev, {
      id: '',
      title: '',
      description: '',
      start_date: '',
      files: []
    }]);
  }, []);

  const handleSubUnitsDragEnd = useCallback(async (result: any) => {
    const { active, over } = result;
    if (active.id !== over.id) {
      setSubUnits(prev => {
        const activeId = active.data.current.id;
        const overId = over.data.current.id;
        const oldIndex = prev.findIndex(subUnit => String(subUnit.id) === String(activeId));
        const newIndex = prev.findIndex(subUnit => String(subUnit.id) === String(overId));
        const newSubUnits = arrayMove(prev, oldIndex, newIndex);
        // Note each SUB UNIT ROW WILL UPDATE THE ORDER IN THE DATABASE
        // LOOK FOR METHOD: onChangeOrder()
        return newSubUnits;
      });
    }
  }, []);

  const renderSubUnits = useCallback(() => {
    return (
      <SortableContext
        items={subUnits.map((subUnit: SubUnit) => `sub_unit_${subUnit.id.toString()}`)}
        strategy={verticalListSortingStrategy}
      >
        {subUnits.map((subUnit: SubUnit, index: number) => (
          <SubRow
            index={index + 1}
            key={subUnit.id}
            unitId={unit.id}
            data={{
              id: subUnit.id,
              title: subUnit.title,
              description: subUnit.description,
              files: subUnit.files || []
            }}
            onSave={handleSaveSubUnit}
            onDelete={handleDeleteSubUnit}
          />
        ))}
      </SortableContext>
    )
  }, [subUnits, unit.id]);

  const renderDate = useCallback(() => {
    if (unit.start_date) {
      return dayjs(unit.start_date).format('MMM D');
    } else {
      return 'N/A';
    }
  }, [unit.start_date]);

  if (!unit) return null;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition ? transition.toString() : undefined
  };

  return (
    <div
      ref={setNodeRef}
      className={styles.root}
      style={style}
    >
      <div className={styles.header}>
        <div className={styles.header_left}>
          <IconButton
            color='secondary'
            ref={setActivatorNodeRef}
            {...listeners}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant='p' className={styles.date}>
            {renderDate()}
          </Typography>

          <IconButton className={styles.edit_button} onClick={handleEdit}>
            <DriveFileRenameOutlineIcon />
          </IconButton>

          <Typography variant='h4' className={styles.title}>
            {unit.title}
          </Typography>
        </div>

        <div className={styles.header_right}>
          <IconButton className={styles.delete_button} onClick={() => onDelete(unit.id)}>
            <DeleteOutlineIcon />
          </IconButton>

          <IconButton className={styles.expand_button} onClick={handleExpand}>
            {isExpanded ? <IoChevronUpOutline /> : <IoChevronDownOutline />}
          </IconButton>
        </div>
      </div>

      {isExpanded && (
        <DndContext onDragEnd={handleSubUnitsDragEnd}>
          <div ref={setNodeRefDroppable} className={styles.expandContainer}>
            {loading && <Spinner />}

            {renderSubUnits()}

            <Button
              color='primary'
              variant='outlined'
              classes={{
                root: styles.addSubUnitButton
              }}
              onClick={handleAddSubUnit}
            >
              {t('courses.new.structure.add_sub_unit')}
            </Button>
          </div>
        </DndContext>
      )}
    </div>
  );
};

export default UnitRow;
